// Copyright (c) 2026 Goffy. This file belongs to Goffy. All Rights Reserved.

import { Modal, Pressable, StyleSheet, Text, View } from 'react-native';

import { useI18n } from '@/i18n';

type ServiceStatusModalProps = {
  open: boolean;
  onClose: () => void;
  missing: string[];
  checkedAt: string | null;
  hasWarnings: boolean;
  warnings: Array<{ timestamp: string; service: string; body: string }>;
};

export const ServiceStatusModal = ({
  open,
  onClose,
  missing,
  checkedAt,
  hasWarnings,
  warnings,
}: ServiceStatusModalProps) => {
  const { t } = useI18n();

  return (
    <Modal transparent visible={open} animationType="fade" onRequestClose={onClose}>
      <Pressable style={styles.backdrop} onPress={onClose}>
        <View style={styles.card}>
          <Text style={styles.title}>{t('serviceStatus.title')}</Text>
          {missing.length > 0 ? (
            <View style={styles.list}>
              {missing.map((name) => (
                <Text key={name} style={styles.item}>
                  {name}
                </Text>
              ))}
            </View>
          ) : (
            <>
              <Text style={styles.okText}>
                {hasWarnings ? t('serviceStatus.warningsFound') : t('serviceStatus.allOk')}
              </Text>
              {hasWarnings && warnings.length > 0 ? (
                <View style={styles.warnings}>
                  {warnings.map((entry, index) => (
                    <View key={`${entry.timestamp}-${index}`} style={styles.warningRow}>
                      <Text style={styles.warningMeta}>
                        {entry.timestamp || t('serviceStatus.unknownTimestamp')}
                      </Text>
                      <Text style={styles.warningMeta}>
                        {entry.service || t('serviceStatus.unknownService')}
                      </Text>
                      <Text style={styles.warningBody} numberOfLines={3}>
                        {entry.body || t('serviceStatus.unknownBody')}
                      </Text>
                    </View>
                  ))}
                </View>
              ) : null}
            </>
          )}
          {checkedAt ? (
            <Text style={styles.meta}>{t('serviceStatus.checkedAt', { time: checkedAt })}</Text>
          ) : null}
          <Pressable style={styles.close} onPress={onClose}>
            <Text style={styles.closeText}>{t('serviceStatus.close')}</Text>
          </Pressable>
        </View>
      </Pressable>
    </Modal>
  );
};

const styles = StyleSheet.create({
  backdrop: {
    flex: 1,
    backgroundColor: 'rgba(7,12,24,0.5)',
    justifyContent: 'center',
    alignItems: 'center',
  },
  card: {
    minWidth: 260,
    padding: 18,
    borderRadius: 12,
    backgroundColor: '#0b1224',
    borderWidth: 1,
    borderColor: '#3a3f52',
    shadowColor: '#000',
    shadowOpacity: 0.35,
    shadowRadius: 12,
    shadowOffset: { width: 0, height: 6 },
    elevation: 8,
  },
  title: {
    color: '#f4f6fb',
    fontSize: 16,
    fontWeight: '700',
    marginBottom: 10,
  },
  list: {
    marginBottom: 12,
    gap: 6,
  },
  item: {
    color: '#ffd9dd',
    fontSize: 14,
  },
  okText: {
    color: '#b7f5c7',
    fontSize: 14,
    marginBottom: 12,
  },
  warnings: {
    marginBottom: 12,
    gap: 10,
  },
  warningRow: {
    gap: 4,
  },
  warningMeta: {
    color: '#f9d8a6',
    fontSize: 12,
  },
  warningBody: {
    color: '#f3f4f6',
    fontSize: 12,
  },
  meta: {
    color: '#8a94b2',
    fontSize: 12,
    marginBottom: 12,
  },
  close: {
    alignSelf: 'flex-end',
    paddingVertical: 6,
    paddingHorizontal: 10,
    borderRadius: 8,
    backgroundColor: '#1b2544',
  },
  closeText: {
    color: '#e6edff',
    fontWeight: '600',
  },
});
